import { useQuery } from 'react-query';
import groupBy from 'lodash/groupBy';
import find from 'lodash/find';
import { useMemo } from 'react';
import { getAvailableSpots } from 'services/CustomerService';
import { useRecoilValue } from 'recoil';
import aptiveUserAccountId from 'app-recoil/selectors/aptive-user-account-id';
import { CustomerSpot, SpotWindow } from 'types/request';

const findSampleSpot = (spots: CustomerSpot[], window: SpotWindow): CustomerSpot | undefined => {
  return find(spots, spot => spot.attributes.window === window);
};

const getSampleSpots = (spots: CustomerSpot[]): Record<SpotWindow, CustomerSpot | undefined> => ({ 
  AT: findSampleSpot(spots, 'AT') || undefined, 
  AM: findSampleSpot(spots, 'AM') || undefined, 
  PM: findSampleSpot(spots, 'PM') || undefined 
});

const groupByDay = (spots: CustomerSpot[]) => {
  if (!spots) return {};
  const groupedByDay = groupBy(spots, spot => spot.attributes.date);
  const result: Record<string, Record<string, CustomerSpot | undefined>> = {};
  Object.keys(groupedByDay).forEach(date => (result[date] = getSampleSpots(groupedByDay[date])));
  return result;
};

export const anytimeSpotCounterpart = (spot: CustomerSpot): CustomerSpot | undefined => {
  if (['AM', 'PM'].includes(spot.attributes.window)) {
    return {
      ...spot,
      attributes: {
        ...spot.attributes,
        window: 'AT',
      },
    };
  }
  return undefined;
};

const useGetAvailableSpots = (enabled = true) => {
  const accountId = useRecoilValue(aptiveUserAccountId);

  const { data, isLoading } = useQuery(['getAvailableSpots'], async () => getAvailableSpots(accountId), {
    enabled,
  });

  const spots = (data || []).reduce((acc: CustomerSpot[], spot) => {
    acc.push(
      ...[
        spot, 
        anytimeSpotCounterpart(spot)
      ].filter(Boolean) as CustomerSpot[]);

    return acc;
  }, []);

  const availableSpots = useMemo(() => (spots ? groupByDay(spots) : {}), [spots]);

  const extractSpots = (date: string, window: SpotWindow) => {
    return spots?.filter(spot => spot.attributes.date === date && spot.attributes.window === window);
  };

  return { availableSpots, isLoading, extractSpots };
};

export default useGetAvailableSpots;
